import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Reorder } from "framer-motion";
import { ImagePlus, Trash2, Loader2, Pencil, X, GripVertical } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fullSrc = (src) => (src.startsWith("/api") ? `${BACKEND}${src}` : src);

export const AdminPortfolio = () => {
  const [pieces, setPieces] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [chapter, setChapter] = useState("01");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [chapterForm, setChapterForm] = useState({ lead: "", rest: "", desc: "" });
  const fileRef = useRef(null);

  const load = async () => {
    const [p, c] = await Promise.all([
      axios.get(`${API}/portfolio`, { withCredentials: true }),
      axios.get(`${API}/portfolio/chapters`, { withCredentials: true }),
    ]);
    setPieces(p.data);
    setChapters(c.data);
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose an image first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("chapter", chapter);
      fd.append("title", title);
      fd.append("file", file);
      await axios.post(`${API}/portfolio`, fd, { withCredentials: true });
      toast.success("Piece added to portfolio");
      setTitle("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this piece from the portfolio page?")) return;
    await axios.delete(`${API}/portfolio/${id}`, { withCredentials: true });
    toast.success("Piece removed");
    load();
  };

  const handleReorder = async (chapterId, newOrder) => {
    setPieces((prev) => [
      ...prev.filter((p) => p.chapter !== chapterId),
      ...newOrder,
    ]);
    try {
      await axios.patch(
        `${API}/portfolio/reorder`,
        { chapter: chapterId, ids: newOrder.map((p) => p.id) },
        { withCredentials: true }
      );
    } catch {
      toast.error("Could not save order");
      load();
    }
  };

  const startEditChapter = (c) => {
    setEditingChapter(c.id);
    setChapterForm({ lead: c.lead, rest: c.rest, desc: c.desc });
  };

  const saveChapter = async (id) => {
    try {
      await axios.patch(`${API}/portfolio/chapters/${id}`, chapterForm, { withCredentials: true });
      toast.success("Chapter updated");
      setEditingChapter(null);
      load();
    } catch {
      toast.error("Could not update chapter");
    }
  };

  if (!pieces) return <p className="text-sm text-[#A6A6B0]">Loading portfolio…</p>;

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">
          Portfolio Manager
        </h1>
        <p className="text-sm text-[#A6A6B0] mt-2">
          {pieces.length} pieces live on your /portfolio page. Drag pieces to reorder, edit chapter
          titles, add or remove — no code needed.
        </p>
      </div>

      <form data-testid="portfolio-upload-form" onSubmit={upload} className="glass-card rounded-lg p-6 sm:p-8 mb-10">
        <h2 className="font-display text-lg font-bold uppercase tracking-tight mb-5">Add a Piece</h2>
        <div className="grid sm:grid-cols-[1fr_1fr] gap-4 mb-4">
          <select
            data-testid="portfolio-chapter-select"
            value={chapter}
            onChange={(e) => setChapter(e.target.value)}
            className="w-full bg-black border border-white/10 rounded-md px-4 py-3 text-sm outline-none focus:border-[#6D00FF] focus:ring-1 focus:ring-[#6D00FF]/60 transition-[border-color,box-shadow] duration-300"
          >
            {chapters.map((c) => (
              <option key={c.id} value={c.id} className="bg-black">
                {c.id} — {c.lead} {c.rest}
              </option>
            ))}
          </select>
          <input
            data-testid="portfolio-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Caption (optional)"
            className="w-full bg-black border border-white/10 rounded-md px-4 py-3 text-sm outline-none focus:border-[#6D00FF] focus:ring-1 focus:ring-[#6D00FF]/60 transition-[border-color,box-shadow] duration-300"
          />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <input
            ref={fileRef}
            data-testid="portfolio-file-input"
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm text-[#A6A6B0] file:mr-4 file:rounded-md file:border-0 file:bg-[#6D00FF]/20 file:text-[#E8B6FF] file:px-4 file:py-2.5 file:text-xs file:font-bold file:uppercase file:tracking-[0.14em] hover:file:bg-[#6D00FF]/30 file:cursor-pointer"
          />
          <button
            data-testid="portfolio-upload-button"
            type="submit"
            disabled={uploading}
            className="flex items-center gap-2 bg-[#6D00FF] hover:bg-[#8B2CFF] disabled:opacity-60 text-white text-xs font-bold uppercase tracking-[0.14em] px-6 py-3 rounded-md transition-colors duration-300"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Add to Portfolio"}
          </button>
        </div>
      </form>

      {chapters.map((c) => {
        const chapterPieces = pieces.filter((p) => p.chapter === c.id);
        return (
          <div key={c.id} className="mb-12">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-xs font-bold uppercase tracking-[0.22em] text-[#8B2CFF]">
                {c.id} — {c.lead} {c.rest} · {chapterPieces.length}
              </h2>
              <button
                data-testid={`chapter-edit-${c.id}`}
                onClick={() => startEditChapter(c)}
                className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#A6A6B0] hover:text-white transition-colors duration-300"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit Chapter
              </button>
            </div>

            {editingChapter === c.id && (
              <div className="glass-card rounded-lg p-5 mb-5 space-y-3" data-testid="chapter-edit-form">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    data-testid="chapter-lead-input"
                    value={chapterForm.lead}
                    onChange={(e) => setChapterForm((f) => ({ ...f, lead: e.target.value }))}
                    placeholder="First word(s) — shown in purple"
                    className="w-full bg-black border border-white/10 rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#6D00FF]"
                  />
                  <input
                    data-testid="chapter-rest-input"
                    value={chapterForm.rest}
                    onChange={(e) => setChapterForm((f) => ({ ...f, rest: e.target.value }))}
                    placeholder="Second word(s) — shown in white"
                    className="w-full bg-black border border-white/10 rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#6D00FF]"
                  />
                </div>
                <input
                  data-testid="chapter-desc-input"
                  value={chapterForm.desc}
                  onChange={(e) => setChapterForm((f) => ({ ...f, desc: e.target.value }))}
                  placeholder="Chapter description"
                  className="w-full bg-black border border-white/10 rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#6D00FF]"
                />
                <div className="flex gap-3">
                  <button
                    data-testid="chapter-save-button"
                    onClick={() => saveChapter(c.id)}
                    className="bg-[#6D00FF] hover:bg-[#8B2CFF] text-white text-xs font-bold uppercase tracking-[0.14em] px-5 py-2.5 rounded-md transition-colors duration-300"
                  >
                    Save Chapter
                  </button>
                  <button
                    onClick={() => setEditingChapter(null)}
                    className="text-xs font-bold uppercase tracking-[0.14em] text-[#A6A6B0] hover:text-white px-4 py-2.5 transition-colors duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {chapterPieces.length === 0 ? (
              <p className="text-sm text-[#A6A6B0]/60">No pieces in this chapter.</p>
            ) : (
              <Reorder.Group
                axis="y"
                values={chapterPieces}
                onReorder={(newOrder) => handleReorder(c.id, newOrder)}
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
              >
                {chapterPieces.map((p, i) => (
                  <Reorder.Item
                    key={p.id}
                    value={p}
                    data-testid={`portfolio-piece-${c.id}-${i}`}
                    className="group relative glass-card rounded-lg overflow-hidden cursor-grab active:cursor-grabbing"
                  >
                    <img
                      src={fullSrc(p.src)}
                      alt={p.title || "Portfolio piece"}
                      loading="lazy"
                      draggable={false}
                      className="w-full aspect-[4/3] object-cover pointer-events-none"
                    />
                    <span className="absolute top-2 left-2 p-1.5 rounded bg-black/60 text-white/50">
                      <GripVertical className="w-3.5 h-3.5" />
                    </span>
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-3 p-3">
                      {p.title && <p className="text-xs text-white text-center">{p.title}</p>}
                      <button
                        data-testid={`portfolio-delete-${c.id}-${i}`}
                        onClick={() => remove(p.id)}
                        className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#C43CFF] border border-[#C43CFF]/40 rounded-md px-4 py-2 hover:bg-[#C43CFF]/10 transition-colors duration-300"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            )}
          </div>
        );
      })}
    </div>
  );
};
